/**
 * Страница управления услугами клиники: список услуг, категории, привязка шаблонов через drag-n-drop
 * Дата: 29.01.2026 - Создан для TODO-038 (Services Module)
 * Дата: 30.01.2026 - Рефакторинг под унифицированные UI компоненты (TODO-045)
 * Дата: 05.02.2026 - TODO-055: Добавлена пагинация и статистика использования услуг
 * Дата: 07.02.2026 - PLAN_06.02.2026_SERVICE_CLUSTERS.md (Итерация 3): Добавлена поддержка кластеров услуг
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/api/medicalServices.ts - API для работы с услугами
 * - frontend/src/hooks/useMedicalServices.ts - хук для управления состоянием услуг
 * - frontend/src/api/serviceClusters.ts - API для работы с кластерами (07.02.2026)
 * - frontend/src/hooks/useServiceClusters.ts - хук для управления кластерами (07.02.2026)
 * - internal/handler/medical_service.go - бэкенд обработчик услуг
 * - internal/handler/service_cluster.go - бэкенд обработчик кластеров (07.02.2026)
 * - components/ui/SearchInput.tsx - унифицированный поиск
 * - components/ui/FilterSelect.tsx - унифицированный фильтр
 * - components/ui/StatusBadge.tsx - унифицированный статус
 * - components/ui/EmptyState.tsx - унифицированное пустое состояние
 * - components/ui/LoadingState.tsx - унифицированная загрузка
 * - components/features/services/ServiceUsageBadges.tsx - шильдики статистики
 * - components/features/services/ClusterPanel.tsx - панель кластеров (07.02.2026)
 * - components/features/services/ServiceClusterBadge.tsx - бейдж кластера на услуге (07.02.2026)
 * 
 * КАК РАБОТАЕТ:
 * - Отображает список услуг, сгруппированных по категориям
 * - Drag-n-drop для привязки шаблонов к услугам
 * - Drag-n-drop для добавления услуг в кластеры (07.02.2026)
 * - Фильтр "Не привязанные услуги"
 * - Фильтр по кластерам (07.02.2026)
 * - Массовый выбор услуг (чекбоксы)
 * - Привязка/отвязка шаблонов к услугам
 * - Привязка/отвязка шаблонов к кластерам (07.02.2026)
 * - Пагинация (05.02.2026)
 * - Статистика использования услуг по годам (05.02.2026)
 * 
 * СХЕМА:
 * ServicesPage → useMedicalServices → medicalServicesApi → backend API
 * ServicesPage → useServiceClusters → serviceClustersApi → backend API (07.02.2026)
 * ServicesPage → SearchInput → фильтрация services
 * ServicesPage → FilterSelect → фильтрация по категории/активности/кластеру
 * ServicesPage → drag-n-drop → linkTemplate/unlinkTemplate
 * ServicesPage → drag-n-drop → addServiceToCluster (07.02.2026)
 * ServicesPage → ServiceUsageBadges → отображение статистики (05.02.2026)
 * ServicesPage → ServiceClusterBadge → отображение кластеров на услуге (07.02.2026)
 * ServicesPage → ClusterPanel → управление кластерами (07.02.2026)
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LinearScreen } from '@/features/linear-canvas/LinearScreen';
import { linearCanvas } from '@/features/linear-canvas/linearCanvas';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  Badge, 
  Pagination,
  SearchInput,
  FilterSelect,
  StatusBadge,
  EmptyState,
  LoadingState,
  DisclosureSection
} from '@/components/ui';
import { useMedicalServices } from '@/hooks/useMedicalServices';
import { GripVertical, TrendingUp, Calendar, Clock, Loader2 } from 'lucide-react';
import { medicalServicesApi, MedicalService, ServiceTemplateLink, ServiceUsageStats } from '@/api/medicalServices';
import { isAxiosError } from 'axios';
import { logger } from '@/utils/logger';
import { TenantSelector } from '@/components/ui/TenantSelector';
import { useTenantScope } from '@/hooks/useTenantScope';
// 07.02.2026 - PLAN_06.02.2026_SERVICE_CLUSTERS.md (Итерация 3): Импорты для кластеров
import {
  useServiceClusters,
  useAddServiceToCluster,
  useRemoveServiceFromCluster,
  useReorderClusterServices,
} from '@/hooks/useServiceClusters';
import {
  serviceClustersApi,
  ServiceCluster,
  ServiceClusterMember,
} from '@/api/service-clusters';
import { ClusterPanel } from '@/components/features/services/ClusterPanel';
import { ServiceClusterBadge } from '@/components/features/services/ServiceClusterBadge';

function formatClusterLoadError(error: unknown): string {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 504) {
      return 'Сервер не успел ответить (504).';
    }
    if (status === 500) {
      return 'Ошибка сервера при загрузке кластеров.';
    }
    if (!error.response) {
      return 'Нет связи с сервером. Проверьте подключение.';
    }
  }
  return 'Не удалось загрузить кластеры услуг.';
}

export function ServicesPage() {
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  
  // Фильтры
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  // 07.02.2026 - Фильтр по кластерам
  const [clusterFilter, setClusterFilter] = useState<string>('');
  const [showUnclusteredOnly, setShowUnclusteredOnly] = useState(false);
  
  // 05.02.2026 - TODO-055: Пагинация
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  
  // 05.02.2026 - TODO-055: Статистика использования
  const [usageStats, setUsageStats] = useState<Record<string, ServiceUsageStats>>({});
  const [usageStatsLoading, setUsageStatsLoading] = useState(false);
  
  // Данные
  const filters = useMemo(() => ({
    category_id: categoryFilter || undefined,
    // 16.03.2026 - УСЛУГИ: cluster_id/unclustered_only фильтруем на backend для корректного total
    cluster_id: clusterFilter || undefined,
    search: searchQuery || undefined,
    is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
    unlinked_only: unlinkedOnly,
    unclustered_only: showUnclusteredOnly,
  }), [categoryFilter, clusterFilter, searchQuery, activeFilter, unlinkedOnly, showUnclusteredOnly]);
  
  // 05.02.2026 - FIX: Пагинация услуг через useMedicalServices
  const { services, total: totalServices, categories, isLoading } = useMedicalServices(
    filters, 
    currentPage * pageSize, 
    pageSize
  );
  const [serviceTemplates, setServiceTemplates] = useState<Record<string, ServiceTemplateLink[]>>({});
  
  // Drag-n-drop
  const [draggedService, setDraggedService] = useState<MedicalService | null>(null);
  
  // 07.02.2026 - PLAN_06.02.2026_SERVICE_CLUSTERS.md (Итерация 3): Кластеры
  const {
    clusters,
    isLoading: clustersLoading,
    isError: clustersLoadFailed,
    error: clustersLoadError,
    refetch: refetchClusters,
    isFetching: clustersFetching,
  } = useServiceClusters(true);
  const addServiceToCluster = useAddServiceToCluster();
  const removeServiceFromCluster = useRemoveServiceFromCluster();
  const reorderClusterServices = useReorderClusterServices();
  
  // Загрузка услуг для каждого кластера
  const [servicesByCluster, setServicesByCluster] = useState<Record<string, ServiceClusterMember[]>>({});
  const [clustersByService, setClustersByService] = useState<Record<string, ServiceCluster[]>>({});
  
  // Логирование просмотра страницы
  useEffect(() => {
    logger.info('page_view', { page: 'services' });
  }, []);
  
  // Загрузка привязок для услуг (batch endpoint для оптимизации N+1)
  // Дата: 29.01.2026 - TODO-038 РЕВЬЮ: заменено на batch endpoint
  useEffect(() => {
    const loadServiceTemplates = async () => {
      try {
        const serviceIds = services.map(s => s.id);
        const data = await medicalServicesApi.getBatchServiceTemplates(serviceIds);
        setServiceTemplates(data.templates_by_service || {});
      } catch (error) {
        // Fallback: инициализируем пустыми массивами
        const links: Record<string, ServiceTemplateLink[]> = {};
        services.forEach(s => { links[s.id] = []; });
        setServiceTemplates(links);
        logger.error('service_templates_batch_load_failed', { error: String(error) });
      }
    };
    
    if (services.length > 0) {
      loadServiceTemplates();
    }
  }, [services]);
  
  // 05.02.2026 - TODO-055: Загрузка ВСЕЙ статистики использования услуг (без пагинации)
  // FIX: Статистика загружается один раз для всех услуг, пагинируются только сами услуги
  useEffect(() => {
    const loadUsageStats = async () => {
      if (!tenantId) return;
      
      setUsageStatsLoading(true);
      try {
        // Загружаем ВСЮ статистику (limit 10000 — достаточно для любой клиники)
        const data = await medicalServicesApi.getUsageStats(0, 10000);
        
        // 06.02.2026 - FIX: Создаём map по НЕСКОЛЬКИМ ключам для надёжного сопоставления
        // Проблема: в UlybkaPRO appointments хранят сокращённые имена ("осмотр"),
        // а services — полные ("Ортодонтический осмотр"). Поэтому нужны все ключи.
        const statsMap: Record<string, ServiceUsageStats> = {};
        data.services.forEach(stat => {
          // По service_id (UUID) — самый надёжный ключ (JOIN services на бэкенде)
          if (stat.service_id) {
            statsMap[stat.service_id] = stat;
          }
          // По названию (для DHC — совпадает, для UlybkaPRO — может не совпадать)
          if (stat.service_name) {
            statsMap[stat.service_name] = stat;
          }
          // По коду если есть
          if (stat.service_code) {
            statsMap[stat.service_code] = stat;
          }
        });
        setUsageStats(statsMap);
        
        logger.info('service_usage_stats_loaded', {
          count: data.services.length,
          total: data.total,
        });
      } catch (error) {
        logger.error('service_usage_stats_load_failed', { error: String(error) });
      } finally {
        setUsageStatsLoading(false);
      }
    };
    
    loadUsageStats();
  }, [tenantId]); // Только при смене тенанта

  // 07.02.2026 - PLAN_06.02.2026_SERVICE_CLUSTERS.md (Итерация 3): Загрузка данных кластеров
  const loadClusterData = useCallback(async () => {
    if (!tenantId || clusters.length === 0) {
      setServicesByCluster({});
      setClustersByService({});
      return;
    }

    try {
      // Загружаем услуги для каждого кластера
      const servicesMap: Record<string, ServiceClusterMember[]> = {};
      const serviceClustersMap: Record<string, ServiceCluster[]> = {};

      await Promise.all(
        clusters.map(async (cluster) => {
          try {
            const servicesData = await serviceClustersApi.getServices(cluster.id, tenantId);

            servicesMap[cluster.id] = servicesData.services || [];

            // Строим обратный индекс: какие кластеры содержат каждую услугу
            (servicesData.services || []).forEach((member) => {
              if (!serviceClustersMap[member.service_id]) {
                serviceClustersMap[member.service_id] = [];
              }
              if (cluster.is_active) {
                serviceClustersMap[member.service_id].push(cluster);
              }
            });
          } catch (error) {
            logger.error('cluster_data_load_failed', {
              cluster_id: cluster.id,
              error: String(error),
            });
          }
        })
      );

      setServicesByCluster(servicesMap);
      setClustersByService(serviceClustersMap);
    } catch (error) {
      logger.error('clusters_data_load_failed', { error: String(error) });
    }
  }, [clusters, tenantId]);

  useEffect(() => {
    loadClusterData();
  }, [loadClusterData]);
  
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery, categoryFilter, activeFilter, unlinkedOnly, clusterFilter, showUnclusteredOnly]);
  
  // 06.02.2026 - FIX: Сортировка по total_usage DESC (самые популярные первые)
  const displayServices = useMemo(() => {
    return [...services].sort((a, b) => {
      const usageA = usageStats[a.id]?.total_usage || usageStats[a.external_id]?.total_usage || usageStats[a.name]?.total_usage || 0;
      const usageB = usageStats[b.id]?.total_usage || usageStats[b.external_id]?.total_usage || usageStats[b.name]?.total_usage || 0;
      return usageB - usageA; // DESC
    });
  }, [services, usageStats]);
  
  // 16.03.2026 - УСЛУГИ: фильтрация по кластеру и "вне кластеров" вынесена на backend,
  // чтобы пагинация и total считались по реальной выборке.
  const filteredServices = useMemo(() => {
    return displayServices;
  }, [displayServices]);

  // Группировка услуг по категориям
  const groupedServices = useMemo(() => {
    const grouped: Record<string, MedicalService[]> = {};
    const uncategorized: MedicalService[] = [];
    
    filteredServices.forEach((service) => {
      if (service.category_id) {
        const category = categories.find(c => c.id === service.category_id);
        const categoryName = category?.name || 'Без категории';
        if (!grouped[categoryName]) {
          grouped[categoryName] = [];
        }
        grouped[categoryName].push(service);
      } else {
        uncategorized.push(service);
      }
    });
    
    if (uncategorized.length > 0) {
      grouped['Без категории'] = uncategorized;
    }
    
    return grouped;
  }, [filteredServices, categories]);
  
  // Drag handlers
  const handleDragStart = (service: MedicalService) => {
    setDraggedService(service);
  };

  // 07.02.2026 - PLAN_06.02.2026_SERVICE_CLUSTERS.md (Итерация 3): Обработчики для кластеров
  const handleDropToCluster = async (clusterId: string, serviceId: string) => {
    try {
      await addServiceToCluster.mutateAsync({
        clusterId,
        data: { service_id: serviceId },
      });
      logger.info('service_added_to_cluster', {
        cluster_id: clusterId,
        service_id: serviceId,
      });
      setDraggedService(null);
      await loadClusterData();
    } catch (error: any) {
      logger.error('service_add_to_cluster_failed', {
        cluster_id: clusterId,
        service_id: serviceId,
        error: error.response?.data?.error || error.message,
      });
    }
  };

  const handleRemoveServiceFromCluster = async (clusterId: string, serviceId: string) => {
    try {
      await removeServiceFromCluster.mutateAsync({ clusterId, serviceId });
      logger.info('service_removed_from_cluster', {
        cluster_id: clusterId,
        service_id: serviceId,
      });
      await loadClusterData();
    } catch (error: any) {
      logger.error('service_remove_from_cluster_failed', {
        cluster_id: clusterId,
        service_id: serviceId,
        error: error.response?.data?.error || error.message,
      });
    }
  };

  const handleReorderClusterServices = async (clusterId: string, nextServices: ServiceClusterMember[]) => {
    try {
      await reorderClusterServices.mutateAsync({
        clusterId,
        data: {
          services: nextServices.map((member, index) => ({
            service_id: member.service_id,
            sort_order: index + 1,
          })),
        },
      });
      logger.info('cluster_services_reordered', {
        cluster_id: clusterId,
        count: nextServices.length,
      });
      await loadClusterData();
    } catch (error: any) {
      logger.error('cluster_services_reorder_failed', {
        cluster_id: clusterId,
        error: error.response?.data?.error || error.message,
      });
    }
  };

  const handleClusterClick = (clusterId: string) => {
    // 16.03.2026 - УСЛУГИ: клик по бейджу кластера включает server-side фильтр
    setShowUnclusteredOnly(false);
    setClusterFilter(clusterId);
    const panel = document.getElementById('service-clusters-panel');
    panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleClusterSelect = (clusterId: string | null) => {
    setShowUnclusteredOnly(false);
    setClusterFilter(clusterId || '');
  };
  
  // Получить привязки для услуги
  const getServiceTemplates = (serviceId: string): ServiceTemplateLink[] => {
    return serviceTemplates[serviceId] || [];
  };
  
  // Проверить, привязана ли услуга
  const isServiceLinked = (serviceId: string): boolean => {
    return getServiceTemplates(serviceId).length > 0;
  };
  
  // 06.02.2026 - FIX: Получить статистику использования услуги
  // Ищем по ID (UUID), external_id (service_code) и name
  const getServiceUsageStats = (service: MedicalService): ServiceUsageStats | undefined => {
    return usageStats[service.id] || usageStats[service.external_id] || usageStats[service.name];
  };
  
  // 05.02.2026 - TODO-055: Цвет в зависимости от количества использований
  const getUsageColor = (count: number): string => {
    if (count === 0) return 'bg-neutral-100 text-neutral-400';
    if (count < 10) return 'bg-blue-50 text-blue-600';
    if (count < 50) return 'bg-blue-100 text-blue-700';
    if (count < 100) return 'bg-emerald-100 text-emerald-700';
    if (count < 500) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700 font-semibold';
  };
  
  return (
    <MainLayout headerTitle="Услуги клиники" disableMainScroll>
      <LinearScreen
        eyebrow="Каталог"
        title="Услуги клиники"
        lead="Кластеры сверху, список услуг раскрывается отдельно. Перетаскивание в кластер работает внутри открытого списка."
        toolbar={
          isSuperAdmin ? (
            <div className="max-w-xs">
              <TenantSelector
                value={selectedTenantId || undefined}
                onChange={setSelectedTenant}
                showLabel={false}
              />
            </div>
          ) : undefined
        }
      >
      <div className="space-y-4">
        {requireTenantSelection && (
          <div className={linearCanvas.notice}>Выберите клинику для просмотра услуг.</div>
        )}
        {services.length === 0 && !isLoading && tenantId && (
          <div className={linearCanvas.notice}>
            Услуги ещё не синхронизированы из МИС. Синхронизация идёт сама после настройки Data Layer, либо добавьте услуги вручную.
          </div>
        )}
        
        {/* Фильтры */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Поиск услуг..."
              />
              
              <FilterSelect
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={categories.map((cat) => ({ value: cat.id, label: cat.name }))}
                placeholder="Все категории"
                allOptionLabel="Все категории"
              />
              
              <FilterSelect
                value={activeFilter}
                onChange={(value) => setActiveFilter(value as 'all' | 'active' | 'inactive')}
                options={[
                  { value: 'active', label: 'Активные' },
                  { value: 'inactive', label: 'Неактивные' },
                ]}
                placeholder="Все услуги"
                allOptionLabel="Все услуги"
              />
              
              {/* 07.02.2026 - Фильтр по кластерам */}
              <FilterSelect
                value={clusterFilter}
                onChange={(value) => {
                  setClusterFilter(value);
                  if (value) {
                    setShowUnclusteredOnly(false);
                  }
                }}
                options={clusters.map((cluster) => ({ value: cluster.id, label: cluster.name }))}
                placeholder="Все кластеры"
                allOptionLabel="Все кластеры"
              />
              
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={unlinkedOnly}
                    onChange={(e) => setUnlinkedOnly(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-neutral-700">Только непривязанные</span>
                </label>
                {/* 07.02.2026 - Фильтр: показать только услуги вне кластеров */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showUnclusteredOnly}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShowUnclusteredOnly(checked);
                      if (checked) {
                        setClusterFilter('');
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-neutral-700">Только вне кластеров</span>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 07.02.2026 - PLAN_06.02.2026_SERVICE_CLUSTERS.md (Итерация 3): Панель кластеров */}
        {tenantId && (
          <div className="space-y-6">
            <ClusterPanel
              clusters={clusters}
              servicesByCluster={servicesByCluster}
              draggedService={draggedService}
              onDrop={handleDropToCluster}
              onRemoveService={handleRemoveServiceFromCluster}
              onReorderServices={handleReorderClusterServices}
              selectedClusterId={clusterFilter || undefined}
              onSelectCluster={handleClusterSelect}
              isLoading={clustersLoading}
              loadError={clustersLoadFailed ? formatClusterLoadError(clustersLoadError) : null}
              onRetry={() => {
                void refetchClusters();
              }}
              isRetrying={clustersFetching && clustersLoadFailed}
            />

            <DisclosureSection
              title="Список услуг"
              description="Каталог услуг скрыт по умолчанию: раскройте его для поиска, drag-and-drop и проверки привязок."
              actions={<Badge variant="secondary">{totalServices.toLocaleString('ru-RU')} всего</Badge>}
              defaultOpen={false}
              contentClassName="space-y-6"
            >
        
              {/* 06.02.2026 - Индикатор загрузки статистики из Data Layer */}
              {usageStatsLoading && !isLoading && (
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="py-3">
                    <div className="flex items-center gap-3 text-blue-700">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm font-medium">Загрузка статистики использования услуг из Data Layer...</span>
                    </div>
                  </CardContent>
                </Card>
              )}
        
              {/* Список услуг по категориям */}
              {isLoading ? (
                <LoadingState />
              ) : Object.keys(groupedServices).length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <EmptyState 
                      type="search"
                      title="Услуги не найдены"
                      description={searchQuery || categoryFilter ? 'Попробуйте изменить параметры поиска' : 'Услуги ещё не синхронизированы из МИС'}
                    />
                  </CardContent>
                </Card>
              ) : (
                Object.entries(groupedServices).map(([categoryName, categoryServices]) => (
                  <Card key={categoryName}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{categoryName}</span>
                        <Badge variant="secondary">{categoryServices.length}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {categoryServices.map((service) => {
                          const isLinked = isServiceLinked(service.id);
                          const serviceClusters = clustersByService[service.id] || [];
                          
                          return (
                            <div
                              key={service.id}
                              className={`rounded-xl border p-4 transition-all ${
                                draggedService?.id === service.id
                                  ? 'opacity-50 border-primary-500'
                                  : serviceClusters.length > 0
                                  ? 'border-primary-200 bg-primary-50/30'
                                  : 'border-neutral-200 hover:border-neutral-300'
                              }`}
                              draggable
                              onDragStart={() => handleDragStart(service)}
                            >
                              <div className="flex items-start gap-3">
                                <GripVertical className="mt-0.5 h-4 w-4 cursor-move text-neutral-400" />
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <h3 className="text-sm font-semibold text-neutral-900">{service.name}</h3>
                                    {!service.is_active && (
                                      <StatusBadge status={false} size="sm" />
                                    )}
                                    {isLinked ? (
                                      <Badge variant="success" className="text-xs">
                                        Есть шаблон
                                      </Badge>
                                    ) : (
                                      <Badge variant="secondary" className="text-xs">
                                        Без шаблона
                                      </Badge>
                                    )}
                                    {usageStatsLoading ? (
                                      <span className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2 py-0.5 text-xs">
                                        <Loader2 className="h-3 w-3 animate-spin text-neutral-400" />
                                        <span className="h-3 w-6 animate-pulse rounded bg-neutral-200" />
                                      </span>
                                    ) : (() => {
                                      const stats = getServiceUsageStats(service);
                                      const total = stats?.total_usage || 0;
                                      return (
                                        <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs ${getUsageColor(total)}`}>
                                          <TrendingUp className="h-3 w-3" />
                                          {total.toLocaleString('ru-RU')}
                                        </span>
                                      );
                                    })()}
                                  </div>

                                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500">
                                    <span>ID: {service.id}</span>
                                    {service.external_id && <span>Код: {service.external_id}</span>}
                                  </div>

                                  {serviceClusters.length > 0 && (
                                    <div className="mt-2">
                                      <ServiceClusterBadge
                                        clusters={serviceClusters}
                                        serviceId={service.id}
                                        onClusterClick={handleClusterClick}
                                        compact
                                      />
                                    </div>
                                  )}
                              
                                  {usageStatsLoading ? (
                                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                      {[1, 2, 3, 4].map((i) => (
                                        <span key={i} className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2 py-0.5">
                                          <span className="h-3 w-12 animate-pulse rounded bg-neutral-200" />
                                        </span>
                                      ))}
                                    </div>
                                  ) : (() => {
                                    const stats = getServiceUsageStats(service);
                                    if (!stats || stats.total_usage === 0) return null;
                                    return (
                                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                                        <span className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2 py-0.5 text-neutral-600">
                                          <Calendar className="h-3 w-3" />
                                          2023: {stats.usage_2023.toLocaleString('ru-RU')}
                                        </span>
                                        <span className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2 py-0.5 text-neutral-600">
                                          <Calendar className="h-3 w-3" />
                                          2024: {stats.usage_2024.toLocaleString('ru-RU')}
                                        </span>
                                        <span className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2 py-0.5 text-neutral-600">
                                          <Calendar className="h-3 w-3" />
                                          2025: {stats.usage_2025.toLocaleString('ru-RU')}
                                        </span>
                                        <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 ${stats.usage_2026 > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-600'}`}>
                                          <Calendar className="h-3 w-3" />
                                          2026: {stats.usage_2026.toLocaleString('ru-RU')}
                                        </span>
                                        {stats.last_used_at && (
                                          <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-blue-600">
                                            <Clock className="h-3 w-3" />
                                            Последний: {stats.last_used_at}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
        
              {/* Дата: 10.02.2026 - Refactored: используем переиспользуемый Pagination */}
              {(displayServices.length > 0 || currentPage > 0) && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="text-sm text-neutral-600">
                        Страница {currentPage + 1} · Показано {displayServices.length} услуг
                        {totalServices > 0 && <span className="text-neutral-400"> (всего: {totalServices})</span>}
                        {usageStatsLoading && (
                          <span className="ml-2 inline-flex items-center gap-1 text-blue-600">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            загрузка статистики...
                          </span>
                        )}
                      </div>

                      <Pagination
                        offset={currentPage * pageSize}
                        limit={pageSize}
                        total={totalServices}
                        onOffsetChange={(offset) => setCurrentPage(Math.floor(offset / pageSize))}
                        showPageNumbers
                        disabled={isLoading}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
            </DisclosureSection>
          </div>
        )}
      </div>
      </LinearScreen>
    </MainLayout>
  );
}
